import React, { useCallback, useMemo } from 'react';

import { JSONSchema7, JSONSchema7Definition } from 'json-schema';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IconButton, Tooltip } from '@mui/material';

import FlatImageEditor from '~/components/Common/Images/FlatImageEditor';
import {
  FlatImageEditorProps,
  FlatImageExtraField,
  FlatImageExtraFieldValue,
  FlatImageItem,
} from '~/components/Common/Images/types';

import { getUploadedImageId, uploadImage } from '~/services/ImageService';

import { ExtendedRJSFormContext } from './useExtendedRJSForm';

type ImageArrayItem = Record<string, unknown>;

interface Props {
  canAdd: boolean;
  canCopyArrayItems?: boolean;
  canDeleteItems?: boolean;
  canReorderItems?: boolean;
  disabled?: boolean;
  fieldDepth: Array<string>;
  formContext?: ExtendedRJSFormContext;
  formData?: Array<unknown>;
  itemControls?: Array<{ canDelete: boolean; canReorder: boolean }>;
  itemSchema?: JSONSchema7;
  onItemCopyTrigger?: (currentItemIndex: number) => void;
  itemUiSchema?: Record<string, unknown>;
  readonly?: boolean;
}

interface ImageArrayFieldConfig {
  descriptionField?: string;
  descriptionLabel: string;
  extraFields: Array<FlatImageExtraField>;
  heroField?: string;
  hiddenField?: string;
  imageField: string;
}

function isObjectDefinition(definition: JSONSchema7Definition | JSONSchema7 | undefined): definition is JSONSchema7 {
  return !!definition && typeof definition === 'object' && !Array.isArray(definition) && !!definition.properties;
}

function getFieldTitle(definition: JSONSchema7Definition | undefined, fallback: string) {
  if (!definition || typeof definition !== 'object' || Array.isArray(definition)) {
    return fallback;
  }

  return definition.title || fallback;
}

function getFieldType(definition: JSONSchema7Definition | undefined): FlatImageExtraField['type'] | null {
  if (!definition || typeof definition !== 'object' || Array.isArray(definition)) {
    return null;
  }

  if (definition.type === 'boolean') {
    return 'checkbox';
  }

  if (definition.type === 'number' || definition.type === 'integer') {
    return 'number';
  }

  if (definition.type === 'string') {
    return 'text';
  }

  return null;
}

function inferFieldTypeFromValue(value: unknown): FlatImageExtraField['type'] | null {
  if (typeof value === 'boolean') {
    return 'checkbox';
  }

  if (typeof value === 'number') {
    return 'number';
  }

  if (typeof value === 'string') {
    return 'text';
  }

  return null;
}

function getDefaultFieldValue(definition: JSONSchema7Definition | undefined): FlatImageExtraFieldValue {
  if (!definition || typeof definition !== 'object' || Array.isArray(definition)) {
    return undefined;
  }

  if (definition.default !== undefined) {
    return definition.default as FlatImageExtraFieldValue;
  }

  switch (definition.type) {
    case 'boolean':
      return false;
    case 'string':
      return '';
    default:
      return undefined;
  }
}

function getUiFieldWidget(fieldUiSchema: unknown) {
  if (!fieldUiSchema || typeof fieldUiSchema !== 'object' || Array.isArray(fieldUiSchema)) {
    return undefined;
  }

  return (fieldUiSchema as Record<string, unknown>)['ui:widget'];
}

function getFieldTypeWithFallbacks(
  definition: JSONSchema7Definition | undefined,
  widget: unknown,
  sampleValue: unknown,
): FlatImageExtraField['type'] | null {
  if (widget === 'markdown-editor') {
    return 'markdown';
  }

  return getFieldType(definition) ?? inferFieldTypeFromValue(sampleValue);
}

function getExtraFieldOrder(fieldKey: string) {
  const order = ['name', 'sponsorName', 'title', 'description'];
  const index = order.indexOf(fieldKey);
  return index === -1 ? order.length : index;
}

function getImageArrayFieldConfig(
  itemSchema?: JSONSchema7,
  itemUiSchema?: Record<string, unknown>,
  sampleItems: Array<ImageArrayItem> = [],
): ImageArrayFieldConfig | null {
  if (!isObjectDefinition(itemSchema)) {
    return null;
  }

  const properties = itemSchema.properties || {};
  const getSampleValue = (fieldKey: string) =>
    sampleItems.find((item) => item[fieldKey] !== undefined && item[fieldKey] !== null)?.[fieldKey];
  const imageFieldEntry =
    Object.entries(properties).find(([fieldKey]) => getUiFieldWidget(itemUiSchema?.[fieldKey]) === 'image-upload') ||
    Object.entries(properties).find(([fieldKey]) => /^(svcImageId|imageId|image_ref|imageRef)$/i.test(fieldKey));

  if (!imageFieldEntry) {
    return null;
  }

  const imageField = imageFieldEntry[0];
  const descriptionFieldEntry =
    Object.entries(properties).find(([fieldKey, definition]) => {
      const inferredFieldType = getFieldTypeWithFallbacks(
        definition,
        getUiFieldWidget(itemUiSchema?.[fieldKey]),
        getSampleValue(fieldKey),
      );
      return inferredFieldType === 'text' && ['caption', 'alt', 'altText'].includes(fieldKey);
    }) || null;
  const descriptionField = descriptionFieldEntry?.[0];
  const hiddenField = Object.keys(properties).find((fieldKey) => /^(hidden|isHidden|is_hidden)$/i.test(fieldKey));
  const heroField = Object.keys(properties).find((fieldKey) => /^(isHero|is_hero|hero)$/i.test(fieldKey));

  const extraFields = Object.entries(properties)
    .filter(([fieldKey]) => !['id', 'order', imageField, descriptionField, hiddenField, heroField].includes(fieldKey))
    .map(([fieldKey, definition]) => {
      const fieldType = getFieldTypeWithFallbacks(
        definition,
        getUiFieldWidget(itemUiSchema?.[fieldKey]),
        getSampleValue(fieldKey),
      );

      if (!fieldType || getUiFieldWidget(itemUiSchema?.[fieldKey]) === 'hidden') {
        return null;
      }

      return {
        key: fieldKey,
        label: getFieldTitle(definition, fieldKey),
        type: fieldType,
      } as FlatImageExtraField;
    })
    .filter((field): field is FlatImageExtraField => !!field)
    .sort((left, right) => {
      const orderDiff = getExtraFieldOrder(left.key) - getExtraFieldOrder(right.key);

      if (orderDiff !== 0) {
        return orderDiff;
      }

      return left.label.localeCompare(right.label);
    });

  return {
    imageField,
    descriptionField,
    descriptionLabel: getFieldTitle(descriptionFieldEntry?.[1], 'Description'),
    extraFields,
    hiddenField,
    heroField,
  };
}

function getDefaultItem(itemSchema?: JSONSchema7) {
  if (!isObjectDefinition(itemSchema)) {
    return {};
  }

  return Object.entries(itemSchema.properties || {}).reduce<Record<string, unknown>>((acc, [fieldKey, definition]) => {
    const defaultValue = getDefaultFieldValue(definition);

    if (defaultValue !== undefined) {
      acc[fieldKey] = defaultValue;
    }

    return acc;
  }, {});
}

function hasStableItemIdentifier(value: unknown): value is string | number {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return false;
}

function getItemId(item: ImageArrayItem, index: number, config: ImageArrayFieldConfig) {
  const explicitId = item.id;

  if (hasStableItemIdentifier(explicitId)) {
    return explicitId;
  }

  const imageValue = item[config.imageField];

  if (hasStableItemIdentifier(imageValue)) {
    return `${String(imageValue)}-${index}`;
  }

  return `${config.imageField}-${index}`;
}

function toFlatImageItem(item: ImageArrayItem, index: number, config: ImageArrayFieldConfig): FlatImageItem {
  const imageValue = item[config.imageField];
  const publicImageId = typeof imageValue === 'string' && imageValue.trim().length > 0 ? imageValue : undefined;

  return {
    id: getItemId(item, index, config),
    order: index + 1,
    publicImageId,
    displayId: publicImageId || String(getItemId(item, index, config)),
    filename: publicImageId,
    description: config.descriptionField ? String(item[config.descriptionField] ?? '') : '',
    descriptionEditable: !!config.descriptionField,
    isHero: config.heroField ? Boolean(item[config.heroField]) : false,
    isHidden: config.hiddenField ? Boolean(item[config.hiddenField]) : false,
    extraFieldValues: config.extraFields.reduce<Record<string, FlatImageExtraFieldValue>>((acc, field) => {
      acc[field.key] = item[field.key] as FlatImageExtraFieldValue;
      return acc;
    }, {}),
  };
}

function toFormDataItem(
  item: FlatImageItem,
  index: number,
  config: ImageArrayFieldConfig,
  itemSchema: JSONSchema7 | undefined,
  currentItems: Array<ImageArrayItem>,
) {
  const existingItem = currentItems.find((currentItem, currentIndex) => {
    return String(getItemId(currentItem, currentIndex, config)) === String(item.id);
  });
  const nextItem: ImageArrayItem = {
    ...getDefaultItem(itemSchema),
    ...existingItem,
    [config.imageField]: item.publicImageId ?? existingItem?.[config.imageField] ?? '',
  };

  if (config.descriptionField) {
    nextItem[config.descriptionField] = item.description ?? '';
  }

  if (config.heroField) {
    nextItem[config.heroField] = item.isHero ?? false;
  }

  if (config.hiddenField) {
    nextItem[config.hiddenField] = item.isHidden ?? false;
  }

  config.extraFields.forEach((field) => {
    const nextValue = item.extraFieldValues?.[field.key];

    if (nextValue !== undefined) {
      nextItem[field.key] = nextValue;
    }
  });

  if ('order' in nextItem || (isObjectDefinition(itemSchema) && !!itemSchema.properties?.order)) {
    nextItem.order = index + 1;
  }

  return nextItem;
}

export function supportsSchemaDrivenImageArray(
  itemSchema?: JSONSchema7,
  itemUiSchema?: Record<string, unknown>,
): boolean {
  return !!getImageArrayFieldConfig(itemSchema, itemUiSchema);
}

export default function SchemaDrivenImageArrayEditor({
  canAdd,
  canCopyArrayItems = false,
  canDeleteItems = true,
  canReorderItems = true,
  disabled = false,
  fieldDepth,
  formContext,
  formData,
  itemControls,
  itemSchema,
  onItemCopyTrigger,
  itemUiSchema,
  readonly = false,
}: Props) {
  const currentItems = useMemo(
    () =>
      Array.isArray(formData)
        ? formData.filter((item): item is ImageArrayItem => !!item && typeof item === 'object')
        : [],
    [formData],
  );
  const config = useMemo(
    () => getImageArrayFieldConfig(itemSchema, itemUiSchema, currentItems),
    [currentItems, itemSchema, itemUiSchema],
  );
  const editorItems = useMemo(
    () =>
      config
        ? currentItems.map((item, index) => ({
            ...toFlatImageItem(item, index, config),
            canDelete: itemControls?.[index]?.canDelete ?? canDeleteItems,
            canReorder: itemControls?.[index]?.canReorder ?? canReorderItems,
          }))
        : [],
    [canDeleteItems, canReorderItems, config, currentItems, itemControls],
  );

  const handleItemsChange: FlatImageEditorProps['onItemsChange'] = useCallback(
    (nextItems, meta) => {
      if (!config) {
        return;
      }

      const mappedItems = nextItems.map((item, index) => toFormDataItem(item, index, config, itemSchema, currentItems));

      formContext?.setFormDataAtPath(fieldDepth, mappedItems, {
        source: `shared-image:${meta?.reason ?? 'items-change'}`,
      });
    },
    [config, currentItems, fieldDepth, formContext, itemSchema],
  );

  const handleSetHero = useCallback(
    (itemId: string | number) => {
      if (!config?.heroField || !formContext) {
        return;
      }

      const heroField = config.heroField;
      let hasChanged = false;
      const nextItems = currentItems.map((item, index) => {
        const currentItemId = getItemId(item, index, config);
        const shouldBeHero = String(currentItemId) === String(itemId);
        const currentValue = Boolean(item[heroField]);

        if (currentValue === shouldBeHero) {
          return item;
        }

        hasChanged = true;

        return {
          ...item,
          [heroField]: shouldBeHero,
        };
      });

      if (!hasChanged) {
        return;
      }

      formContext.setFormDataAtPath(fieldDepth, nextItems, {
        source: 'shared-image:hero-toggle',
      });
    },
    [config, currentItems, formContext, fieldDepth],
  );

  const handleUploadFile = useCallback<FlatImageEditorProps['onUploadFile']>(
    async ({ description, desiredOrder, file }) => {
      if (!canAdd || !config) {
        return;
      }

      const uploadResponse = await uploadImage(file);
      const uploadedImageId = getUploadedImageId(uploadResponse);

      if (!uploadedImageId) {
        throw new Error('Image service did not return an image id');
      }

      return toFlatImageItem(
        {
          ...getDefaultItem(itemSchema),
          [config.imageField]: uploadedImageId,
          ...(config.descriptionField ? { [config.descriptionField]: description } : {}),
        },
        typeof desiredOrder === 'number' ? Math.max(desiredOrder - 1, 0) : currentItems.length,
        config,
      );
    },
    [canAdd, config, currentItems.length, itemSchema],
  );

  if (!config) {
    return null;
  }

  const renderItemActions =
    canCopyArrayItems && onItemCopyTrigger
      ? (item: FlatImageItem) => {
          const currentItemIndex = editorItems.findIndex((editorItem) => String(editorItem.id) === String(item.id));

          if (currentItemIndex === -1) {
            return null;
          }

          return (
            <Tooltip placement="top" title="Copy from sibling">
              <IconButton
                color="warning"
                disabled={disabled || readonly}
                size="small"
                onClick={() => onItemCopyTrigger(currentItemIndex)}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        }
      : undefined;

  return (
    <FlatImageEditor
      deleteActionLabel="Delete image"
      descriptionLabel={config.descriptionLabel}
      disabled={disabled || readonly}
      enablePendingCropUpload
      extraFields={config.extraFields}
      items={editorItems}
      showDeleteAction={canDeleteItems}
      showDescriptionField={!!config.descriptionField}
      showHeroAction={!!config.heroField}
      showHiddenToggle={!!config.hiddenField}
      showReorderAction={canReorderItems}
      uploadLabel="Upload images"
      onItemsChange={handleItemsChange}
      onSetHero={config.heroField && formContext ? handleSetHero : undefined}
      onUploadFile={canAdd ? handleUploadFile : undefined}
      renderItemActions={renderItemActions}
    />
  );
}
